import { ButtonGroup, Fab, FormControlLabel, FormGroup, Grid, IconButton, InputAdornment, Paper, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip } from "@mui/material"
import { Upcharge, UpchargeCreationRequest, PatchAction } from "../../apis/ersys-data/api"
import { useEffect, useState } from "react"
import { useErsysDataApi } from "../ErsysDataApiProvider"
import { showError } from "../../features/errors/Errors"
import { setIsLoading } from "../../features/menus/AppMenu"
import { useDispatch } from "react-redux"
import { useParams } from "react-router-dom"
import { DeleteSharp, AddSharp, EditSharp, CheckSharp, CancelSharp } from "@mui/icons-material"
import NagModal from "../NagModal"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import Button from "@mui/material/Button"
import { useForm } from "react-hook-form"
import FormInputText from "../forms/FormInputText"

interface UpchargesTabProps {
    showSkeleton: () => boolean
    formSpacing: number
}

interface UpchargeCreationModalInput {
    description: string
}

const creationDefaults: UpchargeCreationModalInput = {
    description: "",
}

const UpchargesTab: React.FC<UpchargesTabProps> = ({ showSkeleton, formSpacing }) => {
    const ersysDataApiServices = useErsysDataApi()
    const [upcharges, setUpcharges] = useState<Upcharge[]>([])
    const [loading, setLoading] = useState<boolean>(false)
    const [loaded, setLoaded] = useState<boolean>(false)
    const [nagIsVisible, setNagIsVisible] = useState<boolean>(false)
    const [nagOkFunc, setNagOkFunc] = useState<() => Promise<void>>(async () => { })
    const [showCreation, setShowCreation] = useState<boolean>(false)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [editDescription, setEditDescription] = useState<string>("")
    const [editPrice, setEditPrice] = useState<number>(0) // stored in cents
    const [currentPriceValue, setCurrentPriceValue] = useState<number>(0) // dollar amount being typed
    const [waitingForBlur, setWaitingForBlur] = useState<boolean>(false)
    const dispatch = useDispatch()
    const params = useParams()
    const { handleSubmit, reset, control } = useForm<UpchargeCreationModalInput>({
        defaultValues: creationDefaults,
        reValidateMode: "onSubmit",
    })

    useEffect(() => {
        dispatch(setIsLoading(loading))
    }, [loading, dispatch])

    useEffect(() => {
        if (showSkeleton()) {
            return
        }
        if (!params.id) {
            return
        }
        dispatch(setIsLoading(true))
        const fetch = async () => {
            try {
                const id = parseInt(params.id!, 10)
                const resp = await ersysDataApiServices.inventoryCategoryService.inventoryCategoriesListUpcharges(id)
                setUpcharges(resp.data.items)
            }
            catch (ex: any) {
                dispatch(
                    showError({
                        title: "Unexpected Error occurred",
                        message:
                            "An unexpected error occurred while getting upcharges data. If problem persists please contact IT.",
                    }),
                )
                console.log(ex)
            } finally {
                setLoaded(true)
                dispatch(setIsLoading(false))
            }
        }
        fetch()
    }, [dispatch, ersysDataApiServices.inventoryCategoryService, params.id])

    const renderSkeleton = () => {
        return (
            <FormGroup>
                {[...Array(5)].map((_, i) => (
                    <FormControlLabel
                        control={<Skeleton variant='rectangular' width={200} height={20} sx={{ marginRight: 2 }} />}
                        key={`skele-${i}`}
                        label={<Skeleton variant='rectangular' width={20} height={20} />}
                        sx={{ margin: 1.5 }} />
                )
                )}
            </FormGroup>
        )
    }

    const onDeleteUpcharge = (upcharge: Upcharge) => {
        setNagIsVisible(true)
        setNagOkFunc(() => deleteUpcharge(upcharge))
    }

    const deleteUpcharge = (upcharge: Upcharge) => {
        return async () => {
            const id = parseInt(params.id!, 10)
            try {
                setLoading(true)
                setNagIsVisible(false)
                await ersysDataApiServices.inventoryCategoryService.inventoryCategoriesDeleteUpcharge(id, upcharge.id)
                const resp = await ersysDataApiServices.inventoryCategoryService.inventoryCategoriesListUpcharges(id)
                setUpcharges(resp.data.items)
            }
            catch (ex: any) {
                dispatch(showError({
                    title: "Unable to delete upcharge",
                    message:
                        "Unable to delete upcharge. Please try again.",
                }))
                console.log(ex)
            } finally {
                setLoading(false)
            }
        }
    }

    const onCreate = async (input: UpchargeCreationModalInput) => {
        setLoading(true)
        try {
            const id = parseInt(params.id!, 10)
            setShowCreation(false)
            const req: UpchargeCreationRequest = {
                description: input.description
            }
            await ersysDataApiServices.inventoryCategoryService.inventoryCategoriesCreateUpcharge(id, req)
            const resp = await ersysDataApiServices.inventoryCategoryService.inventoryCategoriesListUpcharges(id)
            setUpcharges(resp.data.items)
            reset()
        }
        catch (ex: any) {
            dispatch(
                showError({
                    title: "Unexpected Error occurred",
                    message:
                        "An unexpected error occurred while creating upcharge. If problem persists please contact IT.",
                }),
            )
            console.log(ex)
        }
        finally {
            setLoading(false)
        }
    }

    const transformInput = (n: number): string => {
        return (n / 100).toFixed(2)
    }

    const transformOutput = (n: number): number => {
        return Math.round(n * 100)
    }

    const startEdit = (upcharge: Upcharge) => {
        setEditingId(upcharge.id)
        setEditDescription(upcharge.description)
        setEditPrice(upcharge.price) // price is in cents
        setCurrentPriceValue(upcharge.price / 100) // initialize dollar amount
        setWaitingForBlur(false)
    }

    const cancelEdit = () => {
        setEditingId(null)
        setEditDescription("")
        setEditPrice(0)
        setCurrentPriceValue(0)
        setWaitingForBlur(false)
    }

    const handlePriceChange = (value: number) => {
        setCurrentPriceValue(value)
        setWaitingForBlur(true)
    }

    const handlePriceBlur = () => {
        setEditPrice(transformOutput(currentPriceValue))
        setWaitingForBlur(false)
    }

    const saveEdit = async (upchargeId: number) => {
        setLoading(true)
        try {
            const id = parseInt(params.id!, 10)
            const patchSet: PatchAction[] = [
                {
                    operationType: "Replace",
                    to: "description",
                    value: editDescription,
                },
                {
                    operationType: "Replace",
                    to: "price",
                    value: editPrice,
                }
            ]
            await ersysDataApiServices.inventoryCategoryService.inventoryCategoriesUpdateUpcharge(id, upchargeId, { actions: patchSet })
            const resp = await ersysDataApiServices.inventoryCategoryService.inventoryCategoriesListUpcharges(id)
            setUpcharges(resp.data.items)
            setEditingId(null)
            setEditDescription("")
            setEditPrice(0)
        }
        catch (ex: any) {
            dispatch(
                showError({
                    title: "Unexpected Error occurred",
                    message:
                        "An unexpected error occurred while updating upcharge. If problem persists please contact IT.",
                }),
            )
            console.log(ex)
        }
        finally {
            setLoading(false)
        }
    }

    const formatPrice = (price: number): string => {
        return `$${(price / 100).toFixed(2)}`
    }

    return (
        <Grid container alignItems="center" justifyContent="center">
            <Tooltip title="Add Upcharge">
                <Fab
                    onClick={() => setShowCreation(true)}
                    color="primary"
                    aria-label="add"
                    sx={{ position: 'absolute', bottom: 25, right: 25 }}>
                    <AddSharp />
                </Fab>
            </Tooltip>
            <Dialog
                open={showCreation}
                onClose={() => setShowCreation(false)}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <DialogTitle id="alert-dialog-title">Create a new upcharge</DialogTitle>
                <DialogContent>
                    <form>
                        <Grid container spacing={2} sx={{ marginTop: 1 }}>
                            <Grid xs={12} item>
                                <FormInputText
                                    spacing={formSpacing}
                                    name={"description"}
                                    control={control}
                                    label={"Description"}
                                    rules={{ required: "Required Field", maxLength: 200 }}
                                />
                            </Grid>
                        </Grid>
                    </form>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSubmit(onCreate)} autoFocus>
                        Ok
                    </Button>
                    <Button onClick={() => {
                        setShowCreation(false)
                        reset()
                    }} autoFocus>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
            <NagModal
                message='Are you sure?'
                title="Delete Upcharge"
                onCancel={() => { setNagIsVisible(false) }}
                onOk={nagOkFunc!}
                open={nagIsVisible}
            />
            {showSkeleton() || !loaded ? renderSkeleton() :
                <TableContainer sx={{ minWidth: 650, maxHeight: 550 }} component={Paper}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Description</TableCell>
                                <TableCell>Price</TableCell>
                                <TableCell align="right">
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {upcharges.map((upcharge) => (
                                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }} key={upcharge.id}>
                                    <TableCell>
                                        {editingId === upcharge.id ? (
                                            <TextField
                                                size="small"
                                                fullWidth
                                                value={editDescription}
                                                onChange={(e) => setEditDescription(e.target.value)}
                                            />
                                        ) : (
                                            upcharge.description
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {editingId === upcharge.id ? (
                                            <TextField
                                                size="small"
                                                type="number"
                                                fullWidth
                                                value={waitingForBlur ? currentPriceValue : transformInput(editPrice)}
                                                onChange={(e) => handlePriceChange(parseFloat(e.target.value) || 0)}
                                                onBlur={handlePriceBlur}
                                                InputProps={{
                                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                                }}
                                            />
                                        ) : (
                                            formatPrice(upcharge.price)
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <ButtonGroup>
                                            {editingId === upcharge.id ? (
                                                <>
                                                    <Tooltip title="Save">
                                                        <IconButton onClick={() => saveEdit(upcharge.id)} color="success">
                                                            <CheckSharp></CheckSharp>
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Cancel">
                                                        <IconButton onClick={() => cancelEdit()} color="error">
                                                            <CancelSharp></CancelSharp>
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            ) : (
                                                <>
                                                    <Tooltip title="Edit">
                                                        <IconButton onClick={() => startEdit(upcharge)}>
                                                            <EditSharp></EditSharp>
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton onClick={() => onDeleteUpcharge(upcharge)}>
                                                            <DeleteSharp></DeleteSharp>
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </ButtonGroup>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            }
        </Grid>
    )
}

export default UpchargesTab
